import React, { useState } from "react";
import { Loader } from "lucide-react";

const SimpleLineChart = ({
  title = "",
  data,
  width = 100,
  height = 50,
  stroke = "#0066cc",
}: any) => {
  const [hoveredPoint, setHoveredPoint] = useState<{
    x: number;
    y: number;
    value: number;
  } | null>(null);

  if (data === undefined || data === null) {
    return <Loader size={20} className="animate-spin" />;
  }

  if (!data.length) {
    return;
  }

  // Add padding to prevent cut-off
  const padding = 4; // Padding in pixels
  const graphWidth = width - padding * 2;
  const graphHeight = height - padding * 2;

  const max = Math.max(...data);
  const min = Math.min(...data);
  const len = data.length;

  const pointCoords = data.map((d: number, i: number) => {
    // Apply padding to coordinates
    const x = padding + (i / (len - 1)) * graphWidth;
    const y = padding + graphHeight - ((d - min) / (max - min)) * graphHeight;
    return { x, y, value: d };
  });

  // Create a smooth path using bezier curves
  const createSmoothPath = (points: { x: number; y: number }[]) => {
    if (points.length < 2) return "";

    let path = `M ${points[0].x},${points[0].y}`;

    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];

      // Calculate control points for the curve
      const controlPointX1 = current.x + (next.x - current.x) / 3;
      const controlPointX2 = current.x + ((next.x - current.x) * 2) / 3;

      path += ` C ${controlPointX1},${current.y} ${controlPointX2},${next.y} ${next.x},${next.y}`;
    }

    return path;
  };

  const smoothPath = createSmoothPath(pointCoords);

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    let nearest = null;
    let minDist = Infinity;

    pointCoords.forEach((pt: any) => {
      const dx = pt.x - mouseX;
      const dy = pt.y - mouseY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < minDist) {
        minDist = dist;
        nearest = pt;
      }
    });

    if (minDist <= 5 && nearest) {
      setHoveredPoint(nearest);
    } else {
      setHoveredPoint(null);
    }
  };

  return (
    <div className="relative pb-6">
      {hoveredPoint && (
        <div
          className="absolute z-10 px-1 py-0.5 text-xs bg-white border border-gray-300 rounded"
          style={{
            left: hoveredPoint.x,
            top: hoveredPoint.y - 10,
            transform: "translateX(-50%)",
          }}
        >
          {hoveredPoint.value}
        </div>
      )}
      <svg
        width={width}
        height={height}
        onMouseMove={handleMouseMove}
        onMouseLeave={() => setHoveredPoint(null)}
      >
        <path
          d={smoothPath}
          fill="none"
          stroke={stroke}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      <p className="text-xs absolute bottom-0 w-full text-center">{title}</p>
    </div>
  );
};

export default SimpleLineChart;
